import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, PrimaryButton } from "../../components/button";
import { Card, CardGrid, CardNavigation } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard } from "../../components/dashboard";
import { DataPickerControl, MultiDataPickerControl } from "../../components/data-picker";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { NumberInput, Select, TextArea } from "../../components/form";
import { Icon } from "../../components/icon";
import { JsonControl } from "../../components/json-control";
import { ModelPickerControl } from "../../components/model-picker";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent, PlaygroundDefault } from "../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../components/sizable-container";
import { SpecializedControl } from "../../components/specializedObject";
import { TabControl } from "../../components/tabcontrol";
import { BlockCode, ButtonHeading, MultilingualCodeWrapper, MutedText, Paragraph, TextHeading } from "../../components/text";
import { Constants } from "../../constants";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { formatFloat, formatNumber } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { showSuccessToast } from "../../ui/toast";
import { find, indentWhiteSpace, valueFrom } from "../../util";

export default function (ctx) {

    const results = el.state.create(null);

    function getFunctionCallData() {
        return {
            modelName: valueFrom("modelName"),
            reasoningEffort: valueFrom("reasoningEffort"),
            instructions: valueFrom("instructions"),
            inputData: valueFrom("inputData"),
            responseSchema: valueFrom("responseSchema"),
            maxAttempts: valueFrom("maxAttempts"),
            temperature: valueFrom("temperature"),
            tools: valueFrom("enabledTools"),
            toolsOptions: valueFrom("toolOptions"),
            timeout: 240
        };
    }

    function runFunction() {
        runOperation("Running function...", async () => {

            const data = getFunctionCallData();
            const response = await api.postJson("/api/v1/functions/json", data);
            results.value = response.data;
        })
    }

    return Dashboard(
        {
            classList: ["full-size"]
        },
        Card(
            CardNavigation(
                CommandBar(
                    PrimaryButton(Icon("play-mini-fill"), "Run function", {
                        onClick() {
                            runFunction();
                        }
                    }),
                    Button(
                        Icon("quill-pen-ai-line"), "Compose with AI",
                        {
                            onClick() {
                                displayFormControl({
                                    title: "Compose with AI",
                                    message: el.fragment(
                                        el("h2.text-center.text-featured", "Create an intelligent function with the help of AI."),
                                        Paragraph("Describe what you want your function to return, what it should have in its output and what it should expect from an input.")
                                    ),
                                    formControlItem: TextArea(null, null, { placeholder: "I want a function that..." }),
                                    validate: [
                                        [x => x.length > 5, "Please, detail a little more what you want the function to do."]
                                    ],
                                    onConfirm(text) {
                                        runOperation("Composing function...", async () => {
                                            const data = {
                                                userRequest: text
                                            }

                                            const response = await api.postJson("/api/v1/helper-tools/create-function", data);
                                            if (response.data) {
                                                showSuccessToast("Function created successfully!");

                                                const result = response.data;
                                                find("instructions").value = result.instructions;
                                                find("responseSchema").value = result.responseSchema;
                                                find("inputData").value = result.inputData;
                                                find("enabledTools").value = result.tools;
                                            }
                                        });
                                    }
                                })
                            }
                        }
                    ),
                    Button(
                        Icon("book-shelf-line"), "Examples",
                        {
                            onClick() {
                                displayAlert({
                                    title: "Function example library",
                                    dialogSize: "large",
                                    message: el.fragment(
                                        CardGrid(
                                            Button(
                                                ButtonHeading(
                                                    Icon("text-wrap"),
                                                    "Summarize text",
                                                    "A function that summarizes feedback and categorizes whether it needs attention or not."
                                                ),
                                                {
                                                    onClick(event) {
                                                        find("modelName").value = "@google/gemini-2.0-flash";
                                                        find("enabledTools").value = [];
                                                        find("instructions").value = "Summarize the user's comment, creating a short description, with a maximum of 10 words indicating what they want to do. Also, indicate if this comment requires attention or not.";
                                                        find("responseSchema").value = {
                                                            "type": "object",
                                                            "properties": {
                                                                "shortSummary": {
                                                                    "type": "string"
                                                                },
                                                                "requiresAttention": {
                                                                    "type": "boolean"
                                                                }
                                                            },
                                                            "required": ["shortSummary", "requiresAttention"],
                                                            "additionalProperties": false
                                                        };
                                                        find("inputData").value = {
                                                            inputText: "The customer Fernando has been trying to contact support since Friday and says he will cancel if he doesn't speak to someone today." +
                                                                " He also said he is a friend of Rebeca from the commercial and is threatening to speak badly about the company on TikTok. Please, can someone attend to this guy??"
                                                        };
                                                        event.target.closest("dialog").dismiss();
                                                    }
                                                }
                                            ),
                                            Button(
                                                ButtonHeading(
                                                    Icon("hashtag"),
                                                    "Math problem solver",
                                                    "A function that evaluates a human mathematical expression."
                                                ),
                                                {
                                                    onClick(event) {
                                                        find("modelName").value = "@openai/o4-mini";
                                                        find("enabledTools").value = ["Code"];
                                                        find("instructions").value = "Evaluate the given math problem and provide the result.";
                                                        find("responseSchema").value = {
                                                            "type": "object",
                                                            "properties": {
                                                                "steps": {
                                                                    "type": "array",
                                                                    "items": {
                                                                        "type": "object",
                                                                        "properties": {
                                                                            "stepDescription": {
                                                                                "type": "string",
                                                                                "description": "Current math step description."
                                                                            },
                                                                            "carry": {
                                                                                "type": "number",
                                                                                "description": "The current result."
                                                                            }
                                                                        },
                                                                        "required": ["stepDescription", "carry"]
                                                                    },
                                                                    "minItems": 1
                                                                },
                                                                "finalResult": {
                                                                    "type": "number",
                                                                    "description": "The math operation final result."
                                                                }
                                                            },
                                                            "required": ["steps", "finalResult"]
                                                        };
                                                        find("inputData").value = {
                                                            inputText: "what is two plus two minus pi ?"
                                                        };
                                                        event.target.closest("dialog").dismiss();
                                                    }
                                                }
                                            ),
                                            Button(
                                                ButtonHeading(
                                                    Icon("newspaper-line"),
                                                    "News retriever",
                                                    "Searches for the latest news and weather data for the given city."
                                                ),
                                                {
                                                    onClick(event) {
                                                        find("modelName").value = "@openai/gpt-4o-mini";
                                                        find("enabledTools").value = ["WebSearch", "XPostsSearch", "OpenUrl"];
                                                        find("instructions").value = "Search for the 5 latest news and weather data for the given city.";
                                                        find("responseSchema").value = {
                                                            "type": "object",
                                                            "properties": {
                                                                "latestNews": {
                                                                    "type": "array",
                                                                    "items": {
                                                                        "type": "object",
                                                                        "properties": {
                                                                            "title": { "type": "string" },
                                                                            "details": { "type": "string" },
                                                                            "link": { "type": "string", "format": "uri" }
                                                                        },
                                                                        "required": ["title", "details", "link"],
                                                                        "additionalProperties": false
                                                                    }
                                                                },
                                                                "weather": {
                                                                    "type": "object",
                                                                    "properties": {
                                                                        "currentTemperature": { "type": "number" },
                                                                        "currentWeather": {
                                                                            "type": "string",
                                                                            "enum": ["sunny", "cloudy", "rain", "thunderstorm"]
                                                                        },
                                                                        "forecast": {
                                                                            "type": "string",
                                                                            "enum": ["sunny", "cloudy", "rain", "thunderstorm"]
                                                                        }
                                                                    },
                                                                    "required": ["currentTemperature", "currentWeather", "forecast"],
                                                                    "additionalProperties": false
                                                                },
                                                                "assistantSummary": { "type": "string" }
                                                            },
                                                            "required": ["latestNews", "weather", "assistantSummary"],
                                                            "additionalProperties": false
                                                        };
                                                        find("inputData").value = {
                                                            city: "Tokyo"
                                                        };
                                                        event.target.closest("dialog").dismiss();
                                                    }
                                                }
                                            ),
                                            Button(
                                                ButtonHeading(
                                                    Icon("virus-line"),
                                                    "COVID-19 real time data",
                                                    "Brings real-time information about the COVID-19 pandemic."
                                                ),
                                                {
                                                    onClick(event) {
                                                        find("modelName").value = "@openai/gpt-4o-mini";
                                                        find("enabledTools").value = ["WebSearch", "OpenUrl"];
                                                        find("instructions").value = "Bring the count of cases and deaths due to COVID-19.";
                                                        find("responseSchema").value = {
                                                            "type": "object",
                                                            "properties": {
                                                                "world": {
                                                                    "type": "object",
                                                                    "properties": {
                                                                        "cases": { "type": "integer" },
                                                                        "deaths": { "type": "integer" }
                                                                    },
                                                                    "required": ["cases", "deaths"],
                                                                    "additionalProperties": false
                                                                },
                                                                "specifiedCity": {
                                                                    "type": "object",
                                                                    "properties": {
                                                                        "cases": { "type": "integer" },
                                                                        "deaths": { "type": "integer" }
                                                                    },
                                                                    "required": ["cases", "deaths"],
                                                                    "additionalProperties": false
                                                                }
                                                            },
                                                            "required": ["world", "specifiedCity"],
                                                            "additionalProperties": false
                                                        };
                                                        find("inputData").value = {
                                                            city: "Tokyo"
                                                        };
                                                        event.target.closest("dialog").dismiss();
                                                    }
                                                }
                                            )
                                        )
                                    )
                                })
                            }
                        }
                    ),
                    Button(
                        Icon("braces-line"), "View code",
                        {
                            onClick() {
                                displayAlert({
                                    title: "Function code",
                                    dialogSize: "large",
                                    message: el.fragment(
                                        "Use the code below to integrate the function you created.",
                                        MultilingualCodeWrapper("Example", [
                                            {
                                                label: "JavaScript",
                                                language: "javascript",
                                                code: `
export async function aivaxFunction() {
    const data = 
${indentWhiteSpace(JSON.stringify(getFunctionCallData(), null, 4), 4 * 2)};
    
    const toolResponse = await fetch("https://inference.aivax.net/api/v1/functions/json", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": ${'`'}Bearer ${'${'}process.env.AIVAX_API_KEY${'}`'}
        },
        body: JSON.stringify(data)
    });
    
    if (toolResponse.ok) {
        const response = await toolResponse.json();
        const result = response.data.result;
        return result;
    }
    
    throw new Error('Failed to call tool. Status: ' + toolResponse.statusText);
}
                                                    `
                                            },
                                            {
                                                label: "Python",
                                                language: "python",
                                                code: `
import os
import json
import requests

def aivax_function():
    data = ${indentWhiteSpace(JSON.stringify(getFunctionCallData(), null, 4), 4 * 1, true)};
    
    headers = {
        "Content-Type": "application/json",
        "Authorization": f"Bearer {os.getenv('AIVAX_API_KEY')}"
    }
    
    response = requests.post("https://inference.aivax.net/api/v1/functions/json", headers=headers, data=json.dumps(data))
    
    if response.ok:
        result = response.json().get("data", {}).get("result")
        return result
    
    raise Exception(f"Failed to call tool. Status: {response.reason}")
                                                
                                                `
                                            },
                                            {
                                                label: "curl",
                                                language: "bash",
                                                code: `
curl -X POST "https://inference.aivax.net/api/v1/functions/json" \\
    -H "Content-Type: application/json" \\
    -H "Authorization: Bearer $AIVAX_API_KEY" \\
    -d '${indentWhiteSpace(JSON.stringify(getFunctionCallData(), null, 4).replaceAll('\'', '\\\''), 4 * 2, true)}'                                                
                                                `
                                            }
                                        ])
                                    )
                                })
                            }
                        }
                    ),
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        {
                            initialSize: "400px"
                        },
                        PlaygroundAside(
                            TabControl([
                                {
                                    title: "Instructions",
                                    icon: Icon("quote-text"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection(
                                                "Instructions",
                                                TextArea(null, "Summarize the input text", {
                                                    id: "instructions",
                                                    placeholder: "Summarize the input text"
                                                })
                                            ),
                                            SizableSection(
                                                "Input data",
                                                JsonControl({
                                                    id: "inputData",
                                                    value: {
                                                        inputText:
                                                            "What's the weather in Paris?"
                                                    }
                                                })
                                            )
                                        )
                                    )
                                },
                                {
                                    title: "Schema",
                                    icon: Icon("braces-line"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection(
                                                "Response schema",
                                                JsonControl({
                                                    id: "responseSchema",
                                                    value: {
                                                        type: 'object',
                                                        properties: {
                                                            summarizedText: {
                                                                type: 'string',
                                                                minLength: 3,
                                                                maxLength: 50,
                                                                description: "the summarized text"
                                                            }
                                                        },
                                                        required: ['summarizedText']
                                                    }
                                                })
                                            )
                                        )
                                    )
                                },
                                {
                                    title: "Parameters",
                                    icon: Icon("equalizer-2-fill"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection(
                                                "Function parameters",
                                                Editor(
                                                    {
                                                        id: "modelName",
                                                        value: "@google/gemini-2.0-flash",
                                                        renderValue(value) {
                                                            return Button(value);
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: "Choose model",
                                                                message: "Choose the model you want to use in the AI function.",
                                                                dialogSize: "large",
                                                                formControlItem: ModelPickerControl({  value }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Model")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "reasoningEffort",
                                                        value: null,
                                                        renderValue(value) {
                                                            return Button(value || MutedText("Unset"));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: DataPickerControl({
                                                                    data: Constants.NullableReasoningEffortSelectItems,
                                                                    value,
                                                                    allowNone: false
                                                                }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Reasoning effort")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "maxAttempts",
                                                        value: 4,
                                                        renderValue(value) {
                                                            return Button(formatNumber(value));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: NumberInput({ value, min: 1, max: 50, step: 1 }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Max attempts")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "temperature",
                                                        value: null,
                                                        renderValue(value) {
                                                            return Button(value && formatFloat(value) || MutedText("Unset"));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: NumberInput({ value, min: 0, max: 2, step: 0.05 }),
                                                                onConfirm: callback,
                                                                validate: [],
                                                                nullButton: true
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Temperature")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "enabledTools",
                                                        value: [],
                                                        renderValue(value) {
                                                            return Button(value.length && value.length + " tools" || MutedText("No tools"));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: "Choose tools",
                                                                message: "Choose tools to use with your AI function.",
                                                                formControlItem: MultiDataPickerControl({
                                                                    value,
                                                                    data: [
                                                                        {
                                                                            value: "DateTime",
                                                                            text: TextHeading(
                                                                                Icon("time-line"), "Current date and time",
                                                                                Paragraph("Returns the current date, time and day of the week in the configured time zone.")
                                                                            )
                                                                        },
                                                                        {
                                                                            separator: "Web related tools"
                                                                        },
                                                                        {
                                                                            value: "OpenUrl",
                                                                            text: TextHeading(
                                                                                Icon("link"), "Open URLs",
                                                                                Paragraph("Allows the model to access the content of external links.")
                                                                            )
                                                                        },
                                                                        {
                                                                            value: "WebSearch",
                                                                            text: TextHeading(
                                                                                Icon("search-line"), "Web search",
                                                                                Paragraph("Allows the model to search the web.")
                                                                            )
                                                                        },
                                                                        {
                                                                            value: "XPostsSearch",
                                                                            text: TextHeading(
                                                                                Icon("twitter-x-line"), "X Posts search",
                                                                                Paragraph("Allows the model to search for relevant posts on X (aka. Twitter).")
                                                                            )
                                                                        },
                                                                        {
                                                                            value: "AdvancedWebUsage",
                                                                            text: TextHeading(
                                                                                Icon("drag-drop-line"), "Advanced web search",
                                                                                Paragraph("Allows the model to use a fully-featured browser to navigate, interact and search the web.")
                                                                            )
                                                                        },
                                                                        {
                                                                            separator: "Code evaluation"
                                                                        },
                                                                        {
                                                                            value: "Code",
                                                                            text: TextHeading(
                                                                                Icon("braces-line"), "Code execution",
                                                                                Paragraph("Allows the model to execute code and perform mathematical calculations.")
                                                                            )
                                                                        },
                                                                        {
                                                                            separator: "Content generation"
                                                                        },
                                                                        {
                                                                            value: "ImageGeneration",
                                                                            text: TextHeading(
                                                                                Icon("image-ai-line"), "Image Generation",
                                                                                Paragraph("Allows the model to generate images.")
                                                                            )
                                                                        }
                                                                    ]
                                                                }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Tools")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "toolOptions",
                                                        value: {},
                                                        renderValue(value) {
                                                            return Button("Edit config");
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: "Tools configuration",
                                                                message: "Edit specific tool configurations data.",
                                                                formControlItem: SpecializedControl({
                                                                    value,
                                                                    fields: [
                                                                        {
                                                                            name: "DateTimeTimeZone",
                                                                            label: "Current date and time — time zone (IANA)",
                                                                            control: el("input.app-input[type=text]", {
                                                                                value: value?.DateTimeTimeZone ?? "America/Los_Angeles",
                                                                                placeholder: "America/Los_Angeles"
                                                                            })
                                                                        },
                                                                        {
                                                                            name: "WebSearchMode",
                                                                            label: "Web search mode",
                                                                            control: Select({
                                                                                items: [
                                                                                    {
                                                                                        value: "Summarized"
                                                                                    },
                                                                                    {
                                                                                        value: "Full"
                                                                                    }
                                                                                ]
                                                                            })
                                                                        },
                                                                        {
                                                                            name: "WebSearchMaxResults",
                                                                            label: "Web search max results",
                                                                            control: el("input.app-input[type=number][min=1][max=25][step=1]")
                                                                        }
                                                                    ]
                                                                }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Tool options")
                                                    )
                                                )
                                            )
                                        )
                                    )
                                }
                            ], {
                                type: "vertical"
                            })
                        ),
                    ),
                    HorizontalSizableSection(
                        {
                            initialSize: "calc(100% - 400px)"
                        },
                        el.state.listening([results], () =>
                            PlaygroundContent(
                                results.value == null && PlaygroundDefault("play-large-line", "Click the button in the left to run a function."),
                                results.value && BlockCode(JSON.stringify(results.value, null, 2), "json")
                            )
                        )
                    )
                )
            )
        )
    )
}
